/**
 * <lk-rail> — progressive enhancement for a scroll-snap rail.
 * Expected markup inside the element:
 *   .lk-rail                      the scrolling track
 *   [data-dir="prev|next"]        optional arrow buttons
 *   .lk-progress > span           optional progress bar
 */
class LkRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.lk-rail');
    if (!this.track) return;

    this.prev = this.querySelector('[data-dir="prev"]');
    this.next = this.querySelector('[data-dir="next"]');
    this.progress = this.querySelector('.lk-progress');
    this.bar = this.progress ? this.progress.querySelector('span') : null;

    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.update, { passive: true });

    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);

    if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
    if (this.next) this.next.addEventListener('click', () => this.page(1));

    this.update();
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.update);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  page(direction) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollBy({
      left: direction * rtl * this.track.clientWidth * 0.85,
      behavior: reduce ? 'auto' : 'smooth',
    });
  }

  update() {
    const track = this.track;
    const max = track.scrollWidth - track.clientWidth;
    const overflowing = max > 4;
    const position = Math.abs(track.scrollLeft);

    if (this.prev) {
      this.prev.disabled = !overflowing || position <= 2;
      this.prev.hidden = !overflowing;
    }
    if (this.next) {
      this.next.disabled = !overflowing || position >= max - 2;
      this.next.hidden = !overflowing;
    }

    if (this.progress && this.bar) {
      this.progress.hidden = !overflowing;
      if (overflowing) {
        const size = Math.max((track.clientWidth / track.scrollWidth) * 100, 8);
        const ratio = max > 0 ? position / max : 0;
        const offset = ratio * ((100 - size) / size) * 100;
        this.bar.style.setProperty('--lk-progress-size', size + '%');
        this.bar.style.setProperty('--lk-progress-offset', offset + '%');
      }
    }
  }
}

if (!customElements.get('lk-rail')) customElements.define('lk-rail', LkRail);
